import {
  Button,
  Inline,
  Notification,
  Skeleton,
  Stack,
  useModal,
} from '@omnipresentgroup/design-system';
import {
  useCreateCompensationVariantPaymentMutation,
  useEmployee,
} from '@omnipresentgroup/domains';
import { Form, FormContext } from '@omnipresentgroup/flexible-forms';
import {
  InputFieldSkeleton,
  RadioFieldSkeleton,
} from '@omnipresentgroup/shared';
import {
  CompensationSource,
  EmployeeType,
  OnboardingStatusValues,
} from '@omnipresentgroup/types';
import type { IChangeEvent } from '@rjsf/core';
import type { RJSFSchema } from '@rjsf/utils';
import { DateTime } from 'luxon';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { ADDITIONAL_PAY_PATH } from '../../../constants';
import { AddVariantPaymentReviewModal } from './components';
import { useAddVariantPaymentSchema } from './hooks';
import type { AddVariantPaymentFormFields } from './types';

const FORM_ID = 'add-variant-payment-form';
const FIELD_ID_PREFIX = 'root_';

const FormSkeleton = () => {
  return (
    <Stack gap="32">
      <Stack gap="16">
        <RadioFieldSkeleton key={'radio-field-skeleton'} numberOfOptions={3} />
        <InputFieldSkeleton />
        <InputFieldSkeleton withHelper />
        {Array.from({ length: 2 }, (_, index) => (
          <InputFieldSkeleton key={`input-field-skeleton-${index}`} />
        ))}
      </Stack>
      <Inline justify="flex-end">
        <Skeleton width={80} height={40} />
      </Inline>
    </Stack>
  );
};

type Props = {
  employeeId: string;
};

/** TODO: This is for month-on-month only currently.
 * Consider adjusting this for contractual compensation also in the future.
 */
export const AddVariantPaymentForm = ({ employeeId }: Props) => {
  const navigate = useNavigate();

  const { setErrors } = useContext(FormContext);

  const { modalProps } = useModal({});

  const [modalOpen, setModalOpen] = useState(false);

  const { data: employee, isLoading: isLoadingEmployee } = useEmployee({
    id: employeeId,
    enabled: !!employeeId,
  });

  const isEmployeeActive = useMemo(
    () => employee?.onboardingStatus?.status === OnboardingStatusValues.ACTIVE,
    [employee?.onboardingStatus?.status],
  );

  const disabled = useMemo(
    () => !employee || !isEmployeeActive,
    [employee, isEmployeeActive],
  );

  const [formValues, setFormValues] = useState<AddVariantPaymentFormFields>({});

  const {
    schema: flexibleSchema,
    uiSchema: flexibleUiSchema,
    isloading: isLoadingSchema,
  } = useAddVariantPaymentSchema({
    employeeId,
    formValues,
  });

  // For doing a dry run before opening the review modal
  const {
    mutate: createVariantPayment,
    isPending: isCreatingVariantPayment,
    data: createVariantPaymentData,
  } = useCreateCompensationVariantPaymentMutation({
    employeeId,
    onSuccess: (_data, variables) => {
      variables.dryRun && setModalOpen(true);
    },
  });

  const isLoading = useMemo(
    () => isLoadingEmployee || isLoadingSchema,
    [isLoadingEmployee, isLoadingSchema],
  );

  // Calculate the effective date
  const effectiveDate = useMemo(() => {
    if (formValues?.effectiveDate && employee?.employmentDetails?.startDate) {
      const selectedDate = DateTime.fromISO(formValues.effectiveDate);
      const employeeStartDate = DateTime.fromISO(
        employee.employmentDetails.startDate,
      );
      // use employee start date if selected date is in the same month and year
      return selectedDate.hasSame(employeeStartDate, 'year') &&
        selectedDate.hasSame(employeeStartDate, 'month')
        ? employee.employmentDetails.startDate
        : formValues.effectiveDate;
    }
    return formValues?.effectiveDate;
  }, [employee?.employmentDetails?.startDate, formValues?.effectiveDate]);

  // Calculate the end date
  const endDate = useMemo(
    () =>
      (formValues.endDate &&
        !formValues.untilEndOfContract &&
        DateTime.fromISO(formValues.endDate).endOf('month').toISODate()) ||
      undefined,
    [formValues.endDate, formValues.untilEndOfContract],
  );

  const handleFormChange = useCallback(
    (
      { formData }: IChangeEvent<AddVariantPaymentFormFields, RJSFSchema>,
      id?: string,
    ) => {
      if (formData) {
        // Reset dependent fields based on the changed field
        switch (id) {
          case `${FIELD_ID_PREFIX}type`: {
            setFormValues({
              ...formData,
              subtype: undefined,
              recurrence: undefined,
            });
            break;
          }
          case `${FIELD_ID_PREFIX}subtype`: {
            setFormValues({
              ...formData,
              recurrence: undefined,
            });
            break;
          }
          case `${FIELD_ID_PREFIX}recurrence`: {
            setFormValues({
              ...formData,
              effectiveDate: undefined,
              endDate: undefined,
              untilEndOfContract: false,
            });
            break;
          }
          case `${FIELD_ID_PREFIX}effectiveDate`: {
            setFormValues({
              ...formData,
              endDate: undefined,
            });
            break;
          }
          case `${FIELD_ID_PREFIX}untilEndOfContract`: {
            setFormValues({
              ...formData,
              endDate: undefined,
            });
            break;
          }
          default: {
            setFormValues(formData);
            break;
          }
        }
      }
    },
    [],
  );

  const handleFormSubmit = useCallback(
    ({ formData }: IChangeEvent<AddVariantPaymentFormFields, RJSFSchema>) => {
      setFormValues(formData || {});
      // Do a dry run for EOR employees to get prefunding information and potential errors, else show the modal
      if (employee?.employeeType === EmployeeType.EOR) {
        formData &&
          createVariantPayment({
            dryRun: true,
            employeeId,
            source: CompensationSource.MONTH_ON_MONTH,
            type: formData.type!,
            subtype: formData.subtype!,
            amount: {
              amount: formData.amount?.amount!,
              currency: formData.amount?.currency!,
            },
            recurrence: formData.recurrence!,
            effectiveDate: effectiveDate,
            endDate: endDate,
            reason: formData.reason!,
            additionalNotes: formData.additionalNotes,
          });
      } else {
        setModalOpen(true);
      }
    },
    [
      createVariantPayment,
      effectiveDate,
      employee?.employeeType,
      employeeId,
      endDate,
    ],
  );

  // Initialize form values
  useEffect(() => {
    setFormValues({});
    setErrors([]);
  }, [employeeId, setErrors]);

  return isLoading ? (
    <FormSkeleton />
  ) : (
    <Stack gap="32">
      {employee && !isEmployeeActive && (
        <Notification
          id="employee-status-notice"
          type="inline"
          intent="warning"
          title="Selected employee is not active"
          description="You can't add additional pay for an employee that is not active."
        />
      )}
      <Form<AddVariantPaymentFormFields>
        id={FORM_ID}
        schema={flexibleSchema}
        uiSchema={flexibleUiSchema}
        formData={formValues}
        onChange={handleFormChange}
        onSubmit={handleFormSubmit}
        liveOmit
        omitExtraData
        disabled={disabled}
      />
      <Inline justify="flex-end" w="stretch">
        <Button
          type="submit"
          form={FORM_ID}
          disabled={disabled}
          loading={isCreatingVariantPayment}
        >
          Next
        </Button>
      </Inline>
      {formValues && modalOpen && (
        <AddVariantPaymentReviewModal
          employeeId={employeeId}
          formValues={{ ...formValues, effectiveDate, endDate }}
          modalProps={{
            ...modalProps,
            open: modalOpen,
            onOpenChange: setModalOpen,
          }}
          handleCancel={() => setModalOpen(false)}
          handleSuccess={() =>
            navigate(`${ADDITIONAL_PAY_PATH}?employeeId=${employee?.id}`)
          }
          prefunding={createVariantPaymentData?.prefunding}
        />
      )}
    </Stack>
  );
};
